import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Clock,
  FileEdit,
  Loader2,
  Settings2,
  XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import type { BadgeVariant } from "@/components/ui/badge/badge.variants";
import { CurrencyFlag } from "@/components/common/CurrencyFlag";
import { payrollRunStatusToVariant } from "@/utils/payrollRunStatusVariant";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { H6, Subtle } from "@/components/v2/typography";
import { cn } from "@/utils/cn";

export type PayrollRunStatus =
  | "draft"
  | "generating"
  | "in-review"
  | "approved"
  | "processing"
  | "completed"
  | "cancelled"
  | "failed";

export interface PayrollRunCardCurrencyTotal {
  code: string;
  symbol: string;
  employeeCount: number;
  net: number;
}

interface IProps {
  initials?: string;
  name: string;
  period: string;
  meta?: string[];
  currencies: string;
  employeesCount: number;
  totals: PayrollRunCardCurrencyTotal[];
  progressPercent: number;
  payDate: string;
  status: PayrollRunStatus;
  avatarClassName?: string;
  onPrimaryAction?: () => void;
  /** Overrides the status-derived primary button label (e.g. "View"). */
  primaryActionLabel?: string;
  /**
   * Overrides the status badge — a processed run reads "Paid" only when every
   * payable employee is paid, else "Processed · 2 of 15 paid" (B34).
   */
  statusBadge?: { label: string; variant: BadgeVariant } | null;
  className?: string;
}

const buildStatusConfig = (
  t: TFunction,
): Record<
  PayrollRunStatus,
  {
    label: string;
    icon: typeof Clock;
    primaryLabel: string;
  }
> => ({
  draft: {
    label: t("common:status.draft"),
    icon: FileEdit,
    badgeClass: "bg-gray-100 text-gray-600 border border-gray-200",
    iconClass: "text-gray-500",
    primaryLabel: t("company:payrolls.review_payroll"),
  },
  generating: {
    label: t("company:payrolls.generating"),
    icon: Loader2,
    badgeClass:
      "bg-yellow-light-3 text-yellow-600 border border-yellow-light-4",
    iconClass: "text-yellow-600 animate-spin",
    primaryLabel: t("company:payrolls.review_payroll"),
  },
  "in-review": {
    label: t("common:shared_ui.in_review"),
    icon: Clock,
    primaryLabel: t("company:payrolls.review_payroll"),
  },
  approved: {
    label: t("common:status.approved"),
    icon: CheckCircle2,
    primaryLabel: t("company:payrolls.process_payroll"),
  },
  processing: {
    label: t("company:payrolls.processing"),
    icon: Settings2,
    badgeClass: "bg-primary/10 text-primary border border-primary/20",
    iconClass: "text-primary animate-spin",
    primaryLabel: t("company:payrolls.process_payroll"),
  },
  completed: {
    label: t("common:status.completed"),
    icon: CheckCircle2,
    badgeClass:
      "bg-success-100/10 text-success-100 border border-success-100/20",
    iconClass: "text-success-100",
    primaryLabel: t("company:payrolls.view_payroll"),
  },
  cancelled: {
    label: t("common:status.cancelled"),
    icon: XCircle,
    badgeClass: "bg-red-50 text-red-600 border border-red-200",
    iconClass: "text-red-600",
    primaryLabel: t("company:payrolls.view_payroll"),
  },
  failed: {
    label: t("company:payrolls.failed"),
    icon: XCircle,
    badgeClass: "bg-red-50 text-red-600 border border-red-200",
    iconClass: "text-red-600",
    primaryLabel: t("company:payrolls.view_payroll"),
  },
});

const progressColorClass = (percent: number): string => {
  if (percent >= 100) return "bg-success";
  if (percent >= 67) return "bg-primary";
  if (percent >= 34) return "bg-warning";
  return "bg-danger";
};

const computeInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

const formatAmount = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const PayrollRunCard = ({
  initials,
  name,
  period,
  meta,
  currencies,
  employeesCount,
  totals,
  progressPercent,
  payDate,
  status,
  avatarClassName,
  onPrimaryAction,
  primaryActionLabel,
  statusBadge,
  className,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const statusConfig = buildStatusConfig(t)[status];
  const StatusIcon = statusConfig.icon;
  const avatarText = initials ?? computeInitials(name);
  const subtitle = [period, ...(meta ?? [])].filter(Boolean).join(" | ");
  const sortedTotals = [...totals].sort((a, b) => b.net - a.net);

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-xs border border-border bg-white p-4",
        className,
      )}
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "flex size-10 shrink-0 items-center justify-center rounded-xs text-xs font-bold",
            "bg-primary/10 text-primary",
            avatarClassName,
          )}
        >
          {avatarText}
        </div>

        <div className="flex flex-1 flex-col min-w-0">
          <H6 className="font-semibold truncate">{name}</H6>
          <Subtle className="truncate">{subtitle}</Subtle>
        </div>

        <Badge
          variant={statusBadge?.variant ?? payrollRunStatusToVariant(status)}
          dot={false}
          className="shrink-0"
        >
          <StatusIcon className="size-3" />
          {statusBadge?.label ?? statusConfig.label}
        </Badge>
      </div>

      {/* Currencies */}
      <Subtle className="text-xs">{currencies}</Subtle>

      {/* Stats */}
      <div className="rounded-xs bg-muted px-3 py-2">
        <div className="flex items-center justify-between">
          <Subtle className="text-xs">{t("company:payrolls.employees")}</Subtle>
          <p className="text-base font-bold text-gray-900">{employeesCount}</p>
        </div>
      </div>

      {/* Totals by currency */}
      {sortedTotals.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <Subtle className="text-xs">
            {t("company:payrolls.net_pay_by_currency")}
          </Subtle>
          <div className="rounded-xs border border-border divide-y divide-border max-h-[120px] overflow-auto">
            {sortedTotals.map((total) => (
              <div
                key={total.code}
                className="flex items-center justify-between gap-2 px-3 py-1.5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <CurrencyFlag
                    code={total.code}
                    showCode={false}
                    flagClassName="size-3.5"
                  />
                  <span className="text-xs font-semibold text-gray-700 shrink-0">
                    {total.code}
                  </span>
                  <Subtle className="text-[11px] truncate">
                    {t("company:payrolls.employee_count", {
                      count: total.employeeCount,
                    })}
                  </Subtle>
                </div>
                <span className="text-xs font-semibold text-gray-900 shrink-0">
                  {formatAmount(total.net)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <Subtle className="text-xs">
            {t("company:data_transfer.progress_label")}
          </Subtle>
          <Subtle className="text-xs font-medium">{progressPercent}%</Subtle>
        </div>
        <Progress
          value={progressPercent}
          className="h-1.5"
          indicatorClassName={progressColorClass(progressPercent)}
        />
        <Subtle className="text-xs">
          {t("company:payrolls.pay_date_value", { date: payDate })}
        </Subtle>
      </div>

      {/* Actions */}
      <Button
        variant="primary"
        size="sm"
        onClick={onPrimaryAction}
        className="w-full"
      >
        {primaryActionLabel ?? statusConfig.primaryLabel}
      </Button>
    </div>
  );
};
